import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { combineAllWith } from '@shared/combineAllWith';
import { readFiles } from '@shared/fileReaderUtils';
import { filterMap } from '@shared/utils/rxjs';
import { catchError, defer, from, map, mergeMap, Observable, of, toArray } from 'rxjs';
import { ExcelFileTransfer, xlsxImportError } from '../file-transfer-services/excelFileTransfer.service';
import { IFileDataSchema, IImportedData } from '../file-transfer-services/interfaces';
import { IDataType, IItemOperationSetup, IPostTerminalTemplateRequest, IPutTerminalTemplateRequest, ISortSetting, ITerminalTemplate, ITerminalTemplateData, TerminalTemplateAdministrationService } from '../terminaltemplateAdministration.service';
import { TerminalTemplateValidationService } from '../Validation/terminalTemplateValidation.service';
import { FailedParse, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

/** Unchanged from the pre-migration Excel export, so a file already exported in the field still re-imports: `importData` now requires the sheet to be named `schema.name`. */
const sheetName = 'Terminal Templates';
const templateFileName = 'terminal_template_import_template';
const translationPrefix = 'mes.administration.terminaltemplate.import';

/** Cap on in-flight create/update calls. */
const importConcurrency = 5;

const groupListExample: IItemOperationSetup[] = [
	{
		source: 'name',
		title: 'Series',
		dataType: IDataType.String,
		format: '',
		sort: ISortSetting.Ascending,
	},
	{
		source: 'items',
		title: 'Items',
		dataType: IDataType.Number,
		format: '',
		sort: ISortSetting.None,
	},
];

const unitListExample: IItemOperationSetup[] = [
	{
		source: 'T_Article.Number',
		title: 'Item no.',
		dataType: IDataType.String,
		format: '',
		sort: ISortSetting.None,
	},
	{
		source: 'T_Article.ProducedTime',
		title: 'Produced',
		dataType: IDataType.Time,
		format: 'HH:mm',
		sort: ISortSetting.Descending,
	},
];

export interface ITerminalTemplateExportData {
	alias: string;
	name: string | null;
	groupList: string | null;
	unitList: string | null;
	detailView: string | null;
}

/**
 * The single source of truth for terminal-template columns — export, import, and template generation all read it.
 *
 * `groupList` / `unitList` stay `'string'` carrying JSON text: they are `IItemOperationSetup[]`, a shape the Excel schema cannot express, and files already exported in the field hold JSON strings. Parsing lives in this service and shape validation in `TerminalTemplateValidationService`.
 *
 * `comment` values are i18n **keys**; `generateTemplate` resolves them through TranslateService.
 */
export const TerminalTemplateSchema: IFileDataSchema<ITerminalTemplateExportData> = {
	name: sheetName,
	properties: {
		alias: {
			type: 'string',
			required: true,
			width: 20,
			comment: `${translationPrefix}.template-help.alias`,
			example: 'EXAMPLE_TEMPLATE',
		},
		name: {
			type: 'string',
			required: false,
			width: 30,
			comment: `${translationPrefix}.template-help.name`,
			example: 'Example Terminal Template',
		},
		groupList: {
			type: 'string',
			required: false,
			width: 50,
			comment: `${translationPrefix}.template-help.groupList`,
			example: JSON.stringify(groupListExample),
		},
		unitList: {
			type: 'string',
			required: false,
			width: 50,
			comment: `${translationPrefix}.template-help.unitList`,
			example: JSON.stringify(unitListExample),
		},
		detailView: {
			type: 'string',
			required: false,
			width: 50,
			comment: `${translationPrefix}.template-help.detailView`,
			example: '<div><h1>Detail view</h1><p>Example HTML content</p></div>',
		},
	},
};

/** I18n key for a JSON cell that will not parse, per column. */
const invalidJsonKeys: Record<'groupList' | 'unitList', string> = {
	groupList: `${translationPrefix}.errors.invalid-json-grouplist`,
	unitList: `${translationPrefix}.errors.invalid-json-unitlist`,
};

export interface TerminalTemplateImportContext {
	/** Existing templates keyed by alias, snapshotted once before the import. */
	templatesByAlias: Map<string, ITerminalTemplate>;
	/** Alias values already seen in the imported file, to reject duplicates. */
	seenAliases: Set<string>;
}

/** A parsed template paired with its source location throughout validation and persistence. */
export interface TerminalTemplateImportCandidate {
	template: ITerminalTemplate;
	location?: string;
}

/** A row whose create/update succeeded. */
export interface TerminalTemplateImportResult {
	entity: ITerminalTemplate;
	status: ImportStatus.SuccessCreate | ImportStatus.SuccessUpdate;
}

@Injectable({
	providedIn: 'root',
})
export class TerminalTemplateTransferService implements ITransferService<ITerminalTemplate> {
	constructor(
		private terminalTemplateService: TerminalTemplateAdministrationService,
		private terminalTemplateValidationService: TerminalTemplateValidationService,
		private excelFileTransfer: ExcelFileTransfer<ITerminalTemplateExportData>,
		private translator: TranslateService,
	) {}

	public hasTemplate = () => true;

	public getTemplate = (): void => {
		this.excelFileTransfer.generateTemplate(templateFileName, TerminalTemplateSchema);
	};

	public exportData(templates: ITerminalTemplate[]): Observable<ITerminalTemplate[]> {
		// `defer` so the row mapping and the workbook write happen on subscribe. Both can throw —
		// `ExcelFileTransfer` rejects an over-cap cell rather than truncating it — and a throw
		// during construction would escape as a synchronous exception past the catchError below,
		// where a caller subscribing to an Observable cannot see it.
		return defer(() => {
			const dateStamp = new Date().toISOString().substring(0, 10);
			const rows = templates.map((template) => this.toExportRow(template));

			// The filename is date-suffixed; the sheet is always named after the schema, so the
			// exported file still imports.
			return this.excelFileTransfer.exportData(`terminal_templates_${dateStamp}`, rows, TerminalTemplateSchema);
		}).pipe(
			map(() => templates),
			catchError((error) => {
				// Keeps the underlying message, which for an over-cap cell names the entity, the
				// field, and the actual length.
				throw new Error(`Failed to export data: ${error.message}`);
			}),
		);
	}

	importData(file: File | File[], overwrite: boolean = false): Observable<ImportResponse> {
		const ctx: TerminalTemplateImportContext = {
			templatesByAlias: new Map<string, ITerminalTemplate>(),
			seenAliases: new Set<string>(),
		};

		// Threaded by closure and only ever pushed to — every stage below reports into this array,
		// so a failure at any stage reaches the user instead of being discarded.
		const ImportErrors: ImportErrorTypes[] = [];
		const files = Array.isArray(file) ? file : [file];

		return readFiles(files, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			combineAllWith(
				this.terminalTemplateService.getTerminalTemplates().pipe(
					map((response) => {
						(response?.result ?? []).forEach((template) => ctx.templatesByAlias.set(template.alias, template));
						return {}; // nothing to emit — this only fills the context
					}),
				),
			),
			map(([row]) => row),
			filterMap(this.rejectDuplicateAlias(ctx, ImportErrors)),
			filterMap(this.parseItemLists(ImportErrors)),
			filterMap(this.validateTemplate(ImportErrors)),
			mergeMap(this.createOrUpdateTemplate(ctx, overwrite, ImportErrors), importConcurrency),
			toArray(),
			map(
				(results) =>
					({
						successfulImports: results.map(
							(result) =>
								({
									Id: result.entity.alias,
									status: result.status,
								}) as SuccessFullImport,
						),
						errors: ImportErrors,
					}) as ImportResponse,
			),
		);
	}

	importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<IImportedData<ITerminalTemplateExportData>> => {
			const errorList: xlsxImportError[] = [];

			try {
				const rows = this.excelFileTransfer.importData(buffer, TerminalTemplateSchema, errorList);

				errorList.forEach((e) => {
					ImportErrors.push({
						status: ImportStatus.ParsingError,
						message: e.error,
						// The row number is already inside e.error — every message ExcelFileTransfer emits
						// names its own row, so FailedParse stays format-agnostic.
					} as FailedParse);
				});

				return from(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of();
			}
		};
	}

	/** Drops the second and later rows carrying an alias already seen in this file. Reference data is snapshotted before the import, so without this the same alias twice would produce two creates. */
	rejectDuplicateAlias(ctx: TerminalTemplateImportContext, ImportErrors: ImportErrorTypes[]) {
		return (imported: IImportedData<ITerminalTemplateExportData>): IImportedData<ITerminalTemplateExportData> | undefined => {
			const alias = imported.data.alias;

			if (ctx.seenAliases.has(alias)) {
				ImportErrors.push({
					Id: alias,
					location: imported.location,
					status: ImportStatus.ValidationError,
					errors: {
						alias: {
							duplicate: this.translator.instant(`${translationPrefix}.errors.duplicate-alias`, { alias }),
						},
					},
				});
				return undefined;
			}

			ctx.seenAliases.add(alias);
			return imported;
		};
	}

	/**
	 * Turns the two JSON cells into data and drops the row if either will not parse.
	 *
	 * The parsed value is deliberately **not** trusted here: proving it really is `IItemOperationSetup[]` is the next stage's job. Casting _instead of_ validating is defect 3 this service exists to fix.
	 *
	 * `alias` and `name` are passed through untrimmed, unlike the terminal schema's blank-tolerant columns. There is nothing to trim _to_: `ValidatorPatterns.Alias` / `.Description` both reject leading and trailing whitespace, so a whitespace-only cell is a per-field validation error in the import exactly as it is in
	 * the dialog, rather than something silently rewritten.
	 */
	parseItemLists(ImportErrors: ImportErrorTypes[]) {
		return (imported: IImportedData<ITerminalTemplateExportData>): TerminalTemplateImportCandidate | undefined => {
			const { data: row, location } = imported;
			const errors: Record<string, ValidationErrors> = {};

			const groupList = this.parseItemList(row.groupList, 'groupList', errors);
			const unitList = this.parseItemList(row.unitList, 'unitList', errors);

			if (Object.keys(errors).length > 0) {
				ImportErrors.push({
					Id: row.alias,
					location,
					status: ImportStatus.ValidationError,
					errors,
				});
				return undefined;
			}

			const template: ITerminalTemplate = {
				// Assigned by the backend; resolved from reference data, never from the file.
				id: null,
				alias: row.alias,
				// Absence stays absence: the validator, not the mapping, decides whether a missing
				// name is acceptable — and for this entity it is.
				name: row.name ?? undefined,
				data: {
					groupList,
					unitList,
					// Never trimmed and never null-collapsed: this is HTML, so leading whitespace
					// and line endings are content.
					detailView: row.detailView ?? '',
				},
			};
			return { template, location };
		};
	}

	validateTemplate(ImportErrors: ImportErrorTypes[]) {
		return (candidate: TerminalTemplateImportCandidate): TerminalTemplateImportCandidate | undefined => {
			const { template, location } = candidate;
			// The id is assigned by the backend, so it is not validated on import.
			const validationErrors = this.terminalTemplateValidationService.validateTerminalTemplate(template, false);
			if (Object.keys(validationErrors).length > 0) {
				ImportErrors.push({
					Id: template.alias,
					location,
					status: ImportStatus.ValidationError,
					errors: validationErrors,
				});
				return undefined;
			}

			return candidate;
		};
	}

	/** A failed call is reported as `ImportStatus.Failed` naming the alias and the row emits nothing, so it is never counted as a success — the previous stack discarded the result array entirely and reported "0 created, 0 updated, 0 errors" for an import where every POST returned 500 . */
	createOrUpdateTemplate(ctx: TerminalTemplateImportContext, overwrite: boolean, ImportErrors: ImportErrorTypes[]) {
		return (candidate: TerminalTemplateImportCandidate): Observable<TerminalTemplateImportResult> => {
			const { template, location } = candidate;
			const existing = ctx.templatesByAlias.get(template.alias);

			if (existing) {
				if (!overwrite) {
					// Skip rather than overwrite — the shared default across every other entity.
					ImportErrors.push({
						Id: template.alias,
						location,
						status: ImportStatus.Skipped,
						message: this.translator.instant(`${translationPrefix}.errors.exists`, {
							alias: template.alias,
						}),
					});
					return of();
				}

				const request: IPutTerminalTemplateRequest = {
					id: existing.id,
					...this.buildRequest(template, existing),
				};

				return this.terminalTemplateService.updateTerminalTemplate(request).pipe(
					map(
						() =>
							({
								entity: template,
								status: ImportStatus.SuccessUpdate,
							}) as TerminalTemplateImportResult,
					),
					catchError((error) => {
						ImportErrors.push({
							Id: template.alias,
							location,
							status: ImportStatus.Failed,
							message: this.translator.instant(`${translationPrefix}.errors.update-failed`, { alias: template.alias, msg: error.message }),
						});
						return of();
					}),
				);
			}

			return this.terminalTemplateService.createTerminalTemplate(this.buildRequest(template)).pipe(
				map(
					() =>
						({
							entity: template,
							status: ImportStatus.SuccessCreate,
						}) as TerminalTemplateImportResult,
				),
				catchError((error) => {
					ImportErrors.push({
						Id: template.alias,
						location,
						status: ImportStatus.Failed,
						message: this.translator.instant(`${translationPrefix}.errors.create-failed`, { alias: template.alias, msg: error.message }),
					});
					return of();
				}),
			);
		};
	}

	private toExportRow(template: ITerminalTemplate): ITerminalTemplateExportData {
		const data = template.data;

		return {
			alias: template.alias,
			name: template.name ?? null,
			// `null` and `[]` are written differently — an empty cell and the text `[]` — so both
			// survive the round trip as themselves rather than being flattened into one value.
			groupList: data?.groupList == null ? null : JSON.stringify(data.groupList),
			unitList: data?.unitList == null ? null : JSON.stringify(data.unitList),
			detailView: data?.detailView ?? null,
		};
	}

	private parseItemList(raw: string | null | undefined, schemaKey: 'groupList' | 'unitList', errors: Record<string, ValidationErrors>): IItemOperationSetup[] | null {
		if (raw === undefined || raw === null || raw === '') {
			// Absent, not empty. `[]` arrives as the literal text `[]`, and the two mean different
			// things to the API, so an absent cell must not be promoted to an empty list. For
			// `unitList` the validator rejects both anyway.
			return null;
		}

		try {
			const parsed: unknown = JSON.parse(raw);
			// The only cast in the pipeline, and it buys nothing but assignability: whether this
			// really is an IItemOperationSetup[] is decided by TerminalTemplateValidationService on
			// the very next stage, which also rejects JSON that is not an array at all.
			return parsed as IItemOperationSetup[];
		} catch (error: unknown) {
			errors[schemaKey] = {
				invalidJson: this.translator.instant(invalidJsonKeys[schemaKey], {
					error: error instanceof Error ? error.message : 'Unknown error',
				}),
			};
			return null;
		}
	}

	/**
	 * Builds a create/update request. Called once per HTTP call, never cached or resubmitted: `TerminalTemplateAdministrationService` assigns `data.detailView = base64EncodeUnicode(...)` on the object it is handed (`:65`, `:82`), so a reused request — or a component's in-memory template — would be encoded twice. The
	 * raw, un-encoded string goes out; the administration service does the encoding.
	 */
	private buildRequest(template: ITerminalTemplate, existing?: ITerminalTemplate): IPostTerminalTemplateRequest {
		const data: ITerminalTemplateData = {
			groupList: template.data.groupList ?? null,
			unitList: template.data.unitList ?? null,
			// Never null: the administration service's encoder stringifies whatever it is given, so
			// a null here would be stored as the base64 of the text "null".
			detailView: template.data.detailView ?? '',
		};

		// Not part of the file contract, and nothing in the app writes it — but the edit dialog
		// carries it through its deep clone (`edit-terminaltemplate.component.ts:30`, `:166`), so an
		// update that omitted it would let an import wipe a value the UI preserves. Same rule Edge
		// Case 10 states for a terminal's printConfigurations.
		if (existing?.data?.sqlQuery !== undefined) {
			data.sqlQuery = existing.data.sqlQuery;
		}

		return {
			alias: template.alias,
			// The dialogs send `''` for an unnamed template rather than omitting the key, so an
			// update that clears the name says so explicitly instead of leaving it ambiguous.
			name: template.name ?? '',
			data,
		};
	}
}
